import { DeletedTextRun, ImportedXmlComponent, InsertedTextRun, SimpleField, TextRun } from 'docx';
import type { NodeEntry } from 'slate';

import type { DocumentContext } from './document-context';
import { getEditDate, surroundTextWithComments } from './internal';
import type { DOCXInline } from './text';
import { createRunProperties } from './text';
import type { CrossReferenceElement } from '../../../slate-api';
import {
  isInsertInstructionFieldEdit,
  isRemoveInstructionFieldEdit,
  Queries,
} from '../../../slate-api';
import { parseLink } from '../../../util';

export function createCrossReference(
  crossRefEntry: NodeEntry<CrossReferenceElement>,
  options: { context: DocumentContext }
): DOCXInline[] {
  const [crossRef, crossRefPath] = crossRefEntry;
  const { context } = options;
  const { href, instruction, switches } = crossRef;
  const parsedLink = parseLink(href);
  const bookmarkName = parsedLink.type === 'bookmark' ? parsedLink.bookmark : '';
  const switchStr = switches ? ` ${switches}` : '';
  const cachedValue = crossRef.children[0]?.text ?? '';

  const textNode = crossRef.children[0];
  const runProperties = createRunProperties(Queries.getActiveTextProps(textNode));
  const suggestedInsertEdit = crossRef.suggestedEdits?.find(isInsertInstructionFieldEdit);
  const suggestedDeleteEdit = crossRef.suggestedEdits?.find(isRemoveInstructionFieldEdit);

  if (suggestedInsertEdit) {
    const insertedTextRun = new InsertedTextRun({
      ...runProperties,
      author: context.getAuthorName(suggestedInsertEdit.authorId),
      children: [cachedValue],
      date: getEditDate(suggestedInsertEdit),
      id: context.generateEditId(),
    });
    const field = new SimpleField(`${instruction} ${bookmarkName}${switchStr}`);
    field.addChildElement(insertedTextRun);
    return surroundTextWithComments([textNode, crossRefPath], {
      context,
      textRun: field,
    });
  }

  if (suggestedDeleteEdit) {
    const deletedTextRun = new DeletedTextRun({
      ...runProperties,
      author: context.getAuthorName(suggestedDeleteEdit.authorId),
      children: [cachedValue],
      date: getEditDate(suggestedDeleteEdit),
      id: context.generateEditId(),
    });
    const field = new SimpleField(`${instruction} ${bookmarkName}${switchStr}`);
    field.addChildElement(deletedTextRun);
    return surroundTextWithComments([textNode, crossRefPath], {
      context,
      textRun: field,
    });
  }

  return [
    new TextRun({
      ...runProperties,
      children: [
        createFieldChar('begin'),
        createInstrText(`${instruction} ${bookmarkName}${switchStr}`),
        createFieldChar('separate'),
        cachedValue,
        createFieldChar('end'),
      ],
    }),
  ];
}

function createFieldChar(fieldCharType: 'begin' | 'separate' | 'end') {
  return new ImportedXmlComponent('w:fldChar', { 'w:fldCharType': fieldCharType });
}

function createInstrText(instruction: string) {
  const instrText = new ImportedXmlComponent('w:instrText', { 'xml:space': 'preserve' });
  instrText.push(` ${instruction} `);
  return instrText;
}
